import { Container, Grid, Button, TextField, AppBar, Box, Typography, Dialog, DialogActions, DialogContent, DialogContentText } from '@mui/material';
import { ChangeEvent, useState, useEffect, FocusEvent, Dispatch, SetStateAction } from 'react';
import { Link, useNavigate } from "react-router-dom";
import { useIntl } from 'react-intl';
import TagManager from 'react-gtm-module';
import * as IdbKeyVal from 'idb-keyval';
import isEmail from 'validator/lib/isEmail';
import * as CognitoService from '../services/CognitoService';
import * as SignatureService from '../services/SignatureService';
import vokey_design from '../images/Vokey-Design-Black.png';

class PasswordValidator {
    constructor(public eightChars = false, public number = false, public uppercase = false, public lowercase = false, public specialCharacter = false, public matching = false) { }
}

class FormDto {
    constructor(public email = "", public password = "", public verifyPassword = "", public verificationCode = "") { }
}

class ResetPassword {
    constructor(
        public datetime = Date.now(),
        public count = 0
    ) { }
}

export default function ForgotPassword() {
    const isOnLine = window.navigator.onLine;
    const [formDto, setFormDto] = useState(new FormDto());
    const [confirmPassword, setConfirmPassword] = useState('');
    const [emailIsValid, setEmailIsValid] = useState(false);
    const [showEmailError, setShowEmailError] = useState(false);  //don't show email format error till onBlur
    const [passwordValidator, setPasswordValidator] = useState(new PasswordValidator());  //flags for what constitutes a valid password
    const [passwordIsValid, setPasswordIsValid] = useState(false);
    const [pageErrorMessage, setPageErrorMessage] = useState('');
    const [showAuthenticatedPopup, setShowAuthenticatedPopup] = useState(false);
    const [showSpinner, setShowSpinner] = useState(false);
    const [verificationMessage, setVerificationMessage] = useState('');
    const [enterVerificationCode, setEnterVerificationCode] = useState(false);
    const [showVerificationError, setShowVerificationError] = useState(false);
    const [verificationCodeSent, setVerificationCodeSent] = useState(false);
    const [verificationCodeIsValid, setVerificationCodeIsValid] = useState(false);
    const [showUnverifiedEmailError, setShowUnverifiedEmailError] = useState(false);
    const [showEmailNotFoundError, setShowEmailNotFoundError] = useState(false);
    const [userPoolResult, setUserPoolResult] = useState() as [SignatureService.PoolCheck, Dispatch<SetStateAction<SignatureService.PoolCheck>>];

    const intl = useIntl();
    const intlMinCharacters = intl.formatMessage({ id: "general.min.characters" });
    const intlOneNumber = intl.formatMessage({ id: "general.one.number" });
    const intlLowercase = intl.formatMessage({ id: "general.lowercase" });
    const intlUppercase = intl.formatMessage({ id: "general.uppercase" });
    const intlSpecialCharacters = intl.formatMessage({ id: "general.special.characters" });
    const intlPasswords = intl.formatMessage({ id: "general.passwords" });
    const intlCorrectFormErrors = intl.formatMessage({ id: "general.correct.form.errors" });
    const intlForgotPassword = intl.formatMessage({ id: "general.forgot.password" });
    const intlCancel = intl.formatMessage({ id: "general.cancel" });
    const intlConfirmEmail = intl.formatMessage({ id: "general.confirm.email" });
    const intlEmailLc = intl.formatMessage({ id: "general.email.lc" });
    const intlEmailInvalid = intl.formatMessage({ id: "general.email.invalid" });
    const intlEnterVerificationCode = intl.formatMessage({ id: "general.enter.verification.code" });
    const intlVerificationCode = intl.formatMessage({ id: "general.verification.code" });
    const intlResendCode = intl.formatMessage({ id: "general.resend.code" });
    const intlNewPassword = intl.formatMessage({ id: "general.new.password" });
    const intlEnterConfirmationCode = intl.formatMessage({ id: "general.enter.confirmation.code" });
    const intlConfirmPassword = intl.formatMessage({ id: "general.confirm.password" });
    const intlPasswordResetFailed = intl.formatMessage({ id: "general.password.reset.failed" });
    const intlPasswordChangeSucceeded = intl.formatMessage({ id: "general.password.change.succeeded" });
    const intlLoginAgain = intl.formatMessage({ id: "general.login.again" });
    const intlContinue = intl.formatMessage({ id: "general.continue" });
    const intlUnverifiedEmail = intl.formatMessage({ id: "general.forgot.unverifiedEmail" });
    const intlEmailNotFound1 = intl.formatMessage({ id: "general.forgot.email.not.found.1" });
    const intlEmailNotFound2 = intl.formatMessage({ id: "general.forgot.email.not.found.2" });
    const intlEmailNotFound3 = intl.formatMessage({ id: "general.forgot.email.not.found.3" });
    const intlNoInternetConnection = intl.formatMessage({ id: "general.no.connection" });
    const [resetPassword, setResetPassword] = useState(new ResetPassword());
    const RESET_PASSWORD_KEY = "wf-resetPassword";
    const maxResetAttempts = 5;


    useEffect(() => {
        setVerificationMessage(intl.formatMessage({ id: 'general.verification.code' }));
        if (window.location.hostname === 'localhost') {
            setFormDto(new FormDto(""));
            setConfirmPassword('Thooka6M###');
        }
    }, []);

    useEffect(() => {
        (async () => {
            let newReset = await IdbKeyVal.get(RESET_PASSWORD_KEY);
            if (!newReset) {
                newReset = new ResetPassword();
            } else {
                let now = Date.now();
                if (now - newReset.datetime > 10 * 60000) {
                    newReset.count = 0;
                    newReset.datetime = Date.now();
                }
            }
            setResetPassword(newReset);
            await IdbKeyVal.set(RESET_PASSWORD_KEY, newReset);
        })();
    }, []);


    const handleFormChange = ({ target }: ChangeEvent<HTMLInputElement>): void => {
        let newFormDto = { ...formDto, [target.name]: target.value } as FormDto;
        validatePage(newFormDto); //refresh form error messages if necessary
        setFormDto(newFormDto);
    }

    //set the Confirm Password value and compare it to original password
    const handleConfirmPassword = ({ target }: ChangeEvent<HTMLInputElement>): void => {
        setConfirmPassword(target.value);
        validatePassword(formDto.password, target.value);
    }

    const handlePassword = ({ target }: ChangeEvent<HTMLInputElement>): void => {
        let newPassword = target.value;
        let newFormDto = { ...formDto, password: newPassword };
        setFormDto(newFormDto);
        validatePassword(newPassword, confirmPassword);
        validatePage(newFormDto);
    }

    //set validation flags
    const validatePassword = (password: string, confirmPassword: string) => {
        let newPasswordValidator = { ...passwordValidator };
        newPasswordValidator.eightChars = password.length >= 8;
        newPasswordValidator.number = password.match('[0-9]') != null;
        newPasswordValidator.lowercase = password.match('[a-z]') != null;
        newPasswordValidator.uppercase = password.match('[A-Z]') != null;
        newPasswordValidator.specialCharacter = password.match('[\*_\^\.!@\+=#&\$]') != null;
        newPasswordValidator.matching = password !== "" && password === confirmPassword;
        setPasswordValidator(newPasswordValidator);
        let isValid = newPasswordValidator.eightChars && newPasswordValidator.number && newPasswordValidator.lowercase && newPasswordValidator.uppercase && newPasswordValidator.specialCharacter && newPasswordValidator.matching;
        setPasswordIsValid(isValid);
        return isValid;
    }

    const handleEmailBlur = ({ target }: FocusEvent<HTMLInputElement>): void => {
        //can show email is invalid after they exit the input field
        validateEmail(target.value);
        setShowEmailError(true);
    }

    const validatePage = (formDto: FormDto): boolean => {
        let validPassword = validatePassword(formDto.password, confirmPassword);
        let validEmail = validateEmail(formDto.email);
        setVerificationCodeIsValid(formDto.verificationCode !== "");

        let isValid = validPassword && validEmail;
        setPageErrorMessage(!isValid ? intlCorrectFormErrors : "");
        return isValid;
    }


    const validateEmail = (email: string): boolean => {
        let validEmail = email !== "" && isEmail(email.trim());
        setEmailIsValid(validEmail)
        return validEmail;
    }

    const navigate = useNavigate();

    const handleCancel = () => {
        navigate('/apps/wedgefitting');
    }

    const handleDialogClose = () => {
        navigate('/apps/wedgefitting');
    }

    const handleEmailVerificationCheck = async (email: string) => {
        setShowSpinner(true);
        const userPoolCheck: SignatureService.PoolCheck = await SignatureService.poolCheck(email);

        if (!userPoolCheck.IsConfirmed) await CognitoService.resendEmailConfirmationCode(email, userPoolCheck.UserPool);

        setShowSpinner(false);
        setShowEmailNotFoundError(false);
        setShowUnverifiedEmailError(false);
        setVerificationCodeSent(false);

        if (userPoolCheck.UserPool !== "None" && !userPoolCheck.IsConfirmed) {
            setShowUnverifiedEmailError(!userPoolCheck.IsConfirmed);
        } else if (userPoolCheck.UserPool === "None") {
            setShowEmailNotFoundError(true);
        }

        return userPoolCheck;
    }

    const handleSendVerification = async () => {
        //check the password reset limit
        let now = Date.now();
        let newReset = { ...resetPassword };

        //check that they haven't done Reset Password too many times
        //if the time passed is over five minutes, then let them resubmit the request
        if (now - newReset.datetime > 10 * 60000) {
            newReset.count = 0;
            newReset.datetime = Date.now();
        }
        newReset.count++;
        setResetPassword(newReset);
        await IdbKeyVal.set(RESET_PASSWORD_KEY, newReset);

        if (newReset.count < maxResetAttempts && isOnLine && emailIsValid) {
            const trimmedEmail = formDto.email.trim();
            const userPoolCheck: SignatureService.PoolCheck = await handleEmailVerificationCheck(trimmedEmail);

            setUserPoolResult(userPoolCheck)

            if (userPoolCheck.IsConfirmed) {
                TagManager.dataLayer({
                    dataLayer: {
                        event: 'vwf_forgot_password'
                    }
                })
                setShowSpinner(true);
                setTimeout(async () => {
                    await CognitoService.forgotPassword(trimmedEmail, userPoolCheck.UserPool)
                    setVerificationMessage(intlResendCode);
                    setEnterVerificationCode(true);
                    setShowSpinner(false);
                    setVerificationCodeSent(true);
                }, 500)
            }
        }
    }

    const handleSetNewPassword = async () => {
        let response = await CognitoService.confirmNewPassword(formDto.verificationCode, formDto.email.trim(), formDto.password.trim(), userPoolResult.UserPool);
        if (response) {
            TagManager.dataLayer({
                dataLayer: {
                    event: 'vwf_forgot_password_save',
                }
            });
            setShowAuthenticatedPopup(true);
        } else {
            setShowVerificationError(true);
        }
    }

    return (
        <div className='login-form'>
            <div className='clear-navbar'>
                <Container component="main" maxWidth="lg">
                    <Box sx={{ flexGrow: 1 }} className='navbar'>
                        <AppBar position="static" className='appbar'>
                            <Grid container>
                                <Grid item xs={4}>
                                    <Typography className='name' variant="h6" component="div" sx={{ display: { xs: 'none', md: 'flex', marginTop: '15px' } }}>
                                        {intlForgotPassword}
                                    </Typography>

                                </Grid>
                                <Grid item sm={4}>
                                    <img style={{ paddingTop: '15px', width: '300px', alignContent: 'center' }} src={vokey_design} />
                                </Grid>
                                <Grid item sm={4} container justifyContent="flex-end" sx={{ paddingTop: '13px', height: '24px', color: 'black' }}>
                                    <Button disableRipple type="submit" variant="contained" onClick={handleCancel} sx={{ height: '45px' }} className='cancel-button'>{intlCancel}</Button>
                                </Grid>
                            </Grid>
                        </AppBar>
                    </Box>
                </Container>
            </div>

            <Container component="main" maxWidth="lg" className='container' sx={{ textAlign: 'left' }}>
                <Grid container>
                    <Grid item xs={12}>
                        <Typography sx={{ marginBottom: '25px' }}>{intlConfirmEmail}</Typography>
                    </Grid>
                    <Grid item xs={12}>
                        <Typography className='label' sx={{ color: 'black !important' }}>
                            {intlEmailLc}
                        </Typography>
                        <TextField
                            margin="normal"
                            required
                            id="email"
                            name="email"
                            value={formDto.email}
                            onChange={handleFormChange}
                            onBlur={handleEmailBlur}
                            sx={{ width: '400px', textAlign: 'left', marginBottom: '10px !important' }}
                        />
                        <Typography className='label' sx={{ color: 'red !important', marginBottom: '15px', minHeight: '24px' }}>
                            {`${(!emailIsValid && showEmailError) ? intlEmailInvalid : ""}`}
                            {showUnverifiedEmailError ? intlUnverifiedEmail : ""}
                            {showEmailNotFoundError && (
                                <>
                                    {intlEmailNotFound1}{" "}
                                    <Link to="/apps/wedgefitting/createaccount">{intlEmailNotFound2}</Link>{" "}
                                    {intlEmailNotFound3}
                                </>
                            )}
                            {isOnLine ? "" : intlNoInternetConnection}
                        </Typography>
                    </Grid>
                    <Grid item xs={12}>
                        <Button disableRipple type="submit" disabled={!emailIsValid} variant="contained" onClick={handleSendVerification} sx={{ height: '45px', width: '225px !important', marginBottom: '25px' }} className='submit-button'>{verificationMessage}</Button>
                    </Grid>
                    {resetPassword.count > maxResetAttempts && (
                            <Grid item sm={12} className='form-error-label' sx={{ height: '1px' }}>
                                <span>You have requested a new password too many times. Please wait ten minutes and try again.</span>
                            </Grid>
                        )}

                    {resetPassword.count <= maxResetAttempts && verificationCodeSent &&
                        <Grid item xs={12}>
                            <Typography sx={{ marginBottom: '25px' }}>
                                {intlEnterVerificationCode}<br />
                                Please <Link to="/apps/wedgefitting/createaccount">register</Link> to create one.
                            </Typography>
                        </Grid>
                    }
                    <Grid item xs={12}>
                        {showSpinner && <>
                            <Grid item sm={12} className='form-error-label' sx={{ height: '1px', textAlign: 'center !important' }}>
                                <span className="sk-wave" style={{ display: 'inline-flex', marginRight: '0px', marginTop: '10px', textAlign: 'center' }} >
                                    <span className="sk-wave-rect" style={{ backgroundColor: "black" }}></span>
                                    <span className="sk-wave-rect" style={{ backgroundColor: "black" }}></span>
                                    <span className="sk-wave-rect" style={{ backgroundColor: "black" }}></span>
                                    <span className="sk-wave-rect" style={{ backgroundColor: "black" }}></span>
                                    <span className="sk-wave-rect" style={{ backgroundColor: "black" }}></span>
                                </span>
                            </Grid>
                        </>}

                    </Grid>

                    {resetPassword.count <= maxResetAttempts && enterVerificationCode && <>

                        <Grid item xs={12}>
                            <Typography className='label' sx={{ color: 'black !important' }}>
                                {intlVerificationCode}
                            </Typography>
                            <TextField
                                value={formDto.verificationCode}
                                margin="normal"
                                required
                                id="verificationCode"
                                name="verificationCode"
                                onChange={handleFormChange}
                                sx={{ width: '400px', textAlign: 'left' }}
                            />
                        </Grid>
                        <Typography className='label' sx={{ color: 'red !important', marginBottom: '15px', minHeight: '24px' }}>
                            {`${!verificationCodeIsValid ? intlEnterConfirmationCode : ""}`}
                        </Typography>
                        <Grid item xs={6}>
                            <Typography className='label' sx={{ color: 'black !important' }}>
                                {intlNewPassword}
                            </Typography>
                            <TextField
                                value={formDto.password}
                                margin="normal"
                                required
                                id="password"
                                name="password"
                                type="password"
                                onChange={handlePassword}
                                sx={{ width: '400px', textAlign: 'left', marginBottom: '0px !important' }}
                            />

                        </Grid>
                        <Grid item xs={6}>
                            <Typography className='label' sx={{ color: 'black !important' }}>
                                {intlConfirmPassword}
                            </Typography>
                            <TextField
                                value={confirmPassword}
                                margin="normal"
                                required
                                id="confirmPassword"
                                name="confirmPassword"
                                type="password"
                                onChange={handleConfirmPassword}
                                sx={{ width: '400px', textAlign: 'left', marginBottom: '0px !important' }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <ul className="password">
                                <li className={passwordValidator.eightChars ? "valid" : "invalid"}>{intlMinCharacters}</li>
                                <li className={passwordValidator.number ? "valid" : "invalid"}>{intlOneNumber}</li>
                                <li className={passwordValidator.lowercase ? "valid" : "invalid"}>{intlLowercase}</li>
                                <li className={passwordValidator.uppercase ? "valid" : "invalid"}>{intlUppercase}</li>
                                <li className={passwordValidator.specialCharacter ? "valid" : "invalid"}>{intlSpecialCharacters}: <span style={{ fontFamily: "monospace" }}> *_^.!@+=#&$</span></li>
                                <li className={passwordValidator.matching ? "valid" : "invalid"}>{intlPasswords}</li>
                            </ul>
                        </Grid>
                        <Grid item xs={12}>
                            <Button type="submit" disabled={!passwordIsValid || !verificationCodeIsValid} variant="contained" onClick={handleSetNewPassword} sx={{ height: '45px', width: '200px !important', marginBottom: '25px' }} className='submit-button'>Save New Password</Button>
                        </Grid>

                        <Grid item sm={12} className='form-error-label' sx={{ height: '1px' }}>
                            <span>{`${showVerificationError ? intlPasswordResetFailed : ""}`}</span>
                        </Grid>


                    </>}

                </Grid>
            </Container>

            <Dialog
                open={showAuthenticatedPopup}
                onClose={handleDialogClose}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogContent>
                    <DialogContentText id="alert-dialog-description" sx={{ color: 'black', width: '400px' }}>
                        {intlPasswordChangeSucceeded} <br />&nbsp;<br />{intlLoginAgain}
                    </DialogContentText>
                </DialogContent>
                <DialogActions className='appbar'>
                    <Button disableRipple variant='outlined' className='submit-button' onClick={handleDialogClose} sx={{ color: 'black', borderColor: 'black' }}>{intlContinue}</Button>
                </DialogActions>
            </Dialog>
        </div>
    )
}
